import type { TraderProfile } from '@/types/api/legacy-trader-profile'

import { useMemo } from 'react'

import {
  useAdaptedGetDefaultTraderProfileForParentOrgQuery,
  useAdaptedGetTraderProfileQuery,
} from '@/legacy-api/trader-profiles-adapter'
import { useGetSelfQuery } from '@/legacy-api/user-slice'
import { createLogger } from '@/lib/logger'

const LOGGER = createLogger('@/trading/hooks/use-get-resolved-trader-profile')

const indeterminateResult = {
  isOrgDefaultTraderProfileSetup: undefined,
  resolvedTraderProfile: undefined,
}

interface GetResolvedTraderProfileResult {
  /** Whether the organization has a default trader profile setup. This is the
   * only hard requirement for trading — an individual trader no longer needs to
   * set up their own trader profile. */
  isOrgDefaultTraderProfileSetup: boolean | undefined
  /** The complete trader profile used to submit orders. Built by layering the
   * trader's (possibly absent or incomplete) trader profile on top of the
   * organization's default trader profile, while always attributing the order to
   * the current user. */
  resolvedTraderProfile: TraderProfile | undefined
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function useGetResolvedTraderProfile(): GetResolvedTraderProfileResult {
  const {
    data: self,
    isLoading: isSelfLoading,
    isError: isSelfError,
  } = useGetSelfQuery()

  const {
    data: traderProfile,
    isLoading: isTraderProfileLoading,
    isError: isTraderProfileError,
    error: traderProfileError,
  } = useAdaptedGetTraderProfileQuery(
    {
      userId: self?.id || '',
    },
    {
      skip: !self?.id || isSelfLoading || isSelfError,
    },
  )

  const {
    data: defaultOrgTraderProfile,
    isLoading: isDefaultTraderProfileLoading,
    isError: isDefaultTraderProfileError,
    error: defaultTraderProfileError,
  } = useAdaptedGetDefaultTraderProfileForParentOrgQuery()

  return useMemo((): GetResolvedTraderProfileResult => {
    // These are pre-requisites for fetching the trader's trader profile.
    if (isSelfLoading || isSelfError) {
      return indeterminateResult
    }

    // Still waiting on responses from at least one of the queries.
    if (isTraderProfileLoading || isDefaultTraderProfileLoading) {
      return indeterminateResult
    }

    // The organization's default trader profile is the only hard requirement.
    // Without it we cannot resolve venue trader identities, so trading is blocked.
    if (isDefaultTraderProfileError || !defaultOrgTraderProfile) {
      const errorMessage = `Default trader profile not found for parent organization of ${self?.organization_id}`
      LOGGER.error(errorMessage, {
        error: new Error(errorMessage),
        rtkQueryError: defaultTraderProfileError,
      })
      return {
        ...indeterminateResult,
        isOrgDefaultTraderProfileSetup: false,
      }
    }

    // A missing user-level trader profile is no longer blocking: we fall back to
    // the organization default for venue configs / Bloomberg id while attributing
    // the order to the current user. This matches the resolved profile an empty
    // user trader profile already produces.
    if (isTraderProfileError || !traderProfile) {
      const warningMessage = `Trader profile not found for ${self?.id}; falling back to the organization default`
      LOGGER.warn(warningMessage, {
        error: new Error(warningMessage),
        rtkQueryError: traderProfileError,
      })
    }

    const resolvedTraderProfile: TraderProfile = {
      user_id: self?.id || defaultOrgTraderProfile.user_id || '',
      bloomberg_id:
        traderProfile?.bloomberg_id ||
        defaultOrgTraderProfile.bloomberg_id ||
        '',
      venue_trader_configs: {
        ...(defaultOrgTraderProfile.venue_trader_configs || {}),
        ...(traderProfile?.venue_trader_configs || {}),
      },
    }

    return {
      isOrgDefaultTraderProfileSetup: true,
      resolvedTraderProfile,
    }
  }, [
    isSelfError,
    isSelfLoading,
    traderProfile,
    isTraderProfileLoading,
    isTraderProfileError,
    traderProfileError,
    defaultOrgTraderProfile,
    isDefaultTraderProfileLoading,
    defaultTraderProfileError,
    isDefaultTraderProfileError,
    self?.organization_id,
    self?.id,
  ])
}
